import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BaseProvider } from "@/context/BaseContext";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Recebimento from "@/pages/Recebimento";
import Triagem from "@/pages/Triagem";
import Manutencao from "@/pages/Manutencao";
import Envase from "@/pages/Envase";
import Dashboard from "@/pages/Dashboard";
import Expedicao from "@/pages/Expedicao";
import Estoque from "@/pages/Estoque";
import Historico from "@/pages/Historico";
import BancoDados from "@/pages/BancoDados";
import Admin from "@/pages/Admin";
import RelatoriosBI from "@/pages/RelatoriosBI";
import Manual from "@/pages/Manual";
import RomaneioExpedicao from "@/pages/RomaneioExpedicao";
import RomaneioRecebimento from "@/pages/RomaneioRecebimento";
import Portaria from "@/pages/Portaria";
import PortariaTelao from "@/pages/PortariaTelao";
import Balanca from "@/pages/Balanca";
import FornecedorPortal from "@/pages/FornecedorPortal";
import FornecedorAcesso from "@/pages/FornecedorAcesso";
import ClientePortal from "@/pages/ClientePortal";
import ClienteAcesso from "@/pages/ClienteAcesso";
import RastreamentoGPS from "@/pages/RastreamentoGPS";
import AgendamentoDoca from "@/pages/AgendamentoDoca";
import OperacaoLogistica from "@/pages/OperacaoLogistica";
import PrintAgendamento from "@/pages/PrintAgendamento";
import PrintTicketBalanca from "@/pages/PrintTicketBalanca";
import PasswordChangeModal from "@/components/PasswordChangeModal";

// Rota → módulo (mesma chave usada em bases.modules e users.modules)
const ROUTE_MODULE = {
  "/portaria": "portaria", "/portaria/telao": "portaria", "/balanca": "balanca",
  "/logistica": "docas", "/logistica/operacao": "docas",
  "/recebimento": "envase", "/envase": "envase",
  "/triagem": "triagem", "/manutencao": "triagem",
  "/estoque": "armazenagem", "/expedicao": "armazenagem",
  "/rastreamento": "rastreamento",
};

function SemAcesso() {
  return (
    <div className="p-10 max-w-xl" data-testid="sem-acesso">
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF9F0A]">Acesso</div>
      <h1 className="font-display text-4xl font-black mt-1">Nenhum módulo liberado</h1>
      <p className="text-sm text-muted-foreground mt-3">
        Seu usuário ainda não tem nenhuma tela liberada nesta base. Peça ao gestor ou administrador para marcar
        onde você vai atuar (ex.: Portaria, Balança) no Painel ADM.
      </p>
    </div>
  );
}

const GESTAO_ROUTES = new Set(["/dashboard", "/relatorios", "/historico", "/banco-dados"]);

function Protected({ children, roles, bare }) {
  const { user, refresh } = useAuth();
  const loc = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground font-mono animate-pulse">Carregando sessão...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  // FORNECEDOR: área 100% isolada — qualquer outra rota redireciona ao portal
  if (user.role === "fornecedor" && loc.pathname !== "/fornecedor") {
    return <Navigate to="/fornecedor" replace />;
  }
  if (user.role !== "fornecedor" && loc.pathname === "/fornecedor") {
    return <Navigate to="/" replace />;
  }
  // CLIENTE: área 100% isolada read-only
  if (user.role === "cliente" && loc.pathname !== "/cliente") {
    return <Navigate to="/cliente" replace />;
  }
  if (user.role !== "cliente" && loc.pathname === "/cliente") {
    return <Navigate to="/" replace />;
  }
  if (roles && !roles.includes(user.role) && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }
  // Restrição por USUÁRIO (onde atua): bloqueia acesso direto por URL a módulos não marcados
  const mod = ROUTE_MODULE[loc.pathname.split("/").slice(0, 3).join("/")] || ROUTE_MODULE["/" + loc.pathname.split("/")[1]];
  if (mod && user.role !== "admin" && user.modules?.length && !user.modules.includes(mod)) {
    return <Navigate to="/" replace />;
  }
  // Gestor com módulos marcados não acessa as telas gerais de gestão (Dashboard, BI, Histórico, Banco de Dados)
  if (GESTAO_ROUTES.has("/" + loc.pathname.split("/")[1]) && user.role !== "admin" && user.modules?.length) {
    return <Navigate to="/" replace />;
  }
  if (bare) return children;
  return (
    <Layout>
      {children}
      <PasswordChangeModal open={!!user.must_change_password} onDone={refresh} />
    </Layout>
  );
}

function HomeRedirect() {
  const { user } = useAuth();
  const [dest, setDest] = React.useState(null);
  React.useEffect(() => {
    if (!user) return;
    if (user.role === "fornecedor") { setDest("/fornecedor"); return; }
    if (user.role === "cliente") { setDest("/cliente"); return; }
    if (user.role === "admin") { setDest("/dashboard"); return; }
    // Operador e gestor: aterrissa na primeira tela cujo módulo esteja habilitado na base (e marcado no usuário).
    // Gestor de base que opera o WMS (envase/triagem/armazenagem/docas/GPS) sem restrição → Dashboard.
    import("@/lib/api").then(({ api }) => {
      api.get("/bases").then(({ data }) => {
        const b = data.find((x) => x.id === user.base || x.code === user.base);
        const m = b?.modules || {};
        const um = user.modules?.length ? user.modules : null;
        const ok = (k, optIn) => (optIn ? m[k] === true : m[k] !== false) && (!um || um.includes(k));
        const baseTemWms = ["envase", "triagem", "armazenagem", "docas", "rastreamento"].some((k) => m[k] !== false);
        if (user.role === "manager" && baseTemWms && !um) return setDest("/dashboard");
        if (ok("envase")) return setDest("/recebimento");
        if (ok("portaria")) return setDest("/portaria");
        if (ok("balanca", true)) return setDest("/balanca");
        if (ok("triagem")) return setDest("/triagem");
        if (ok("armazenagem")) return setDest("/estoque");
        if (ok("docas")) return setDest("/logistica");
        if (ok("rastreamento")) return setDest("/rastreamento");
        setDest("/sem-acesso");
      }).catch(() => setDest("/recebimento"));
    });
  }, [user]);
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!dest) return null;
  return <Navigate to={dest} replace />;
}

function App() {
  useEffect(() => {
    // Força o título da aba (o script do Emergent tenta sobrescrever no boot)
    document.title = "CHEM·WMS · Envase Químico em Tempo Real";
    const forceTitle = setInterval(() => {
      if (document.title !== "CHEM·WMS · Envase Químico em Tempo Real") {
        document.title = "CHEM·WMS · Envase Químico em Tempo Real";
      }
    }, 500);
    // Após 5s o boot já rodou, para o interval
    setTimeout(() => clearInterval(forceTitle), 5000);
    return () => clearInterval(forceTitle);
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <BaseProvider>
        <BrowserRouter>
          <Toaster position="top-right" theme="dark" />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/dashboard" element={<Protected roles={["manager", "admin"]}><Dashboard /></Protected>} />
            <Route path="/recebimento" element={<Protected><Recebimento /></Protected>} />
            <Route path="/triagem" element={<Protected><Triagem /></Protected>} />
            <Route path="/manutencao" element={<Protected><Manutencao /></Protected>} />
            <Route path="/envase" element={<Protected><Envase /></Protected>} />
            <Route path="/estoque" element={<Protected><Estoque /></Protected>} />
            <Route path="/expedicao" element={<Protected><Expedicao /></Protected>} />
            <Route path="/portaria" element={<Protected><Portaria /></Protected>} />
            <Route path="/portaria/telao" element={<Protected bare><PortariaTelao /></Protected>} />
            <Route path="/balanca" element={<Protected><Balanca /></Protected>} />
            <Route path="/fornecedor" element={<Protected bare><FornecedorPortal /></Protected>} />
            <Route path="/fornecedor/acesso/:token" element={<FornecedorAcesso />} />
            <Route path="/cliente" element={<Protected bare><ClientePortal /></Protected>} />
            <Route path="/cliente/acesso/:token" element={<ClienteAcesso />} />
            <Route path="/rastreamento" element={<Protected><RastreamentoGPS /></Protected>} />
            <Route path="/logistica" element={<Protected><AgendamentoDoca /></Protected>} />
            <Route path="/logistica/operacao" element={<Protected><OperacaoLogistica /></Protected>} />
            <Route path="/logistica/print/:id" element={<Protected bare><PrintAgendamento /></Protected>} />
            <Route path="/balanca/ticket/:id" element={<Protected bare><PrintTicketBalanca /></Protected>} />
            <Route path="/relatorios" element={<Protected roles={["manager", "admin"]}><RelatoriosBI /></Protected>} />
            <Route path="/manual" element={<Protected roles={["admin"]}><Manual /></Protected>} />
            <Route path="/sem-acesso" element={<Protected><SemAcesso /></Protected>} />
            <Route path="/historico" element={<Protected roles={["manager", "admin"]}><Historico /></Protected>} />
            <Route path="/banco-dados" element={<Protected roles={["manager", "admin"]}><BancoDados /></Protected>} />
            <Route path="/admin" element={<Protected roles={["admin", "manager"]}><Admin /></Protected>} />
            <Route path="/romaneio/expedicao/:placa" element={<Protected bare><RomaneioExpedicao /></Protected>} />
            <Route path="/romaneio/recebimento/:remessa" element={<Protected bare><RomaneioRecebimento /></Protected>} />
            <Route path="*" element={<HomeRedirect />} />
          </Routes>
        </BrowserRouter>
        </BaseProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
